import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    Divider,
    IconButton,
    Radio,
    TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useAuth } from "auth/Auth";
import colors from "assets/theme/base/colors";

const OPTION_SX = (selected) => ({
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    px: 1.5,
    py: 1,
    borderRadius: "12px",
    border: `1.5px solid ${selected ? colors.primary.main : colors.grey[300]}`,
    bgcolor: selected ? colors.brand.primaryContainer : colors.white.main,
    cursor: "pointer",
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    "&:hover": {
        borderColor: selected ? colors.primary.main : colors.grey[500],
        bgcolor: selected ? colors.brand.primaryContainer : colors.grey[200],
    },
});

const OrderAddDialog = ({ open, onClose, dialogTitle }) => {
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();

    const [mode, setMode] = useState("new");
    const [orders, setOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [loadingOrders, setLoadingOrders] = useState(false);

    useEffect(() => {
        if (!open) {
            setMode("new");
            setSelectedOrder(null);
            return;
        }

        const fetchOrders = async () => {
            setLoadingOrders(true);
            try {
                const response = await axiosInstance.get("/travel-orders/");
                setOrders(response.data || []);
            } catch (error) {
                console.error("Error fetching travel orders:", error);
                setOrders([]);
            } finally {
                setLoadingOrders(false);
            }
        };

        fetchOrders();
    }, [open, axiosInstance]);

    const handleProceed = () => {
        if (mode === "new") {
            navigate("/travel-orders/new");
            onClose();
            return;
        }

        if (!selectedOrder?.id) {
            return;
        }

        // Open the order detail Travelers section (same idea as trip edit step for package components)
        navigate(`/travel-order/${selectedOrder.id}`);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: "100%",
                    maxWidth: 420,
                    m: 2,
                    borderRadius: "16px",
                    boxShadow: "0 20px 48px rgba(14, 35, 72, 0.16)",
                    overflow: "hidden",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2.5,
                    pt: 1.5,
                    pb: 1.25,
                }}
            >
                <Box
                    component="span"
                    sx={{
                        fontFamily: '"Sora", "Roboto", sans-serif',
                        fontSize: "1.05rem",
                        fontWeight: 700,
                        color: colors.dark.main,
                        lineHeight: 1.3,
                    }}
                >
                    {dialogTitle}
                </Box>
                <IconButton
                    size="small"
                    onClick={onClose}
                    aria-label="Close"
                    sx={{
                        width: 30,
                        height: 30,
                        border: `1px solid ${colors.grey[400]}`,
                        borderRadius: "8px",
                        color: colors.text.main,
                        "&:hover": { bgcolor: colors.grey[200] },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            <Divider sx={{ borderColor: colors.grey[300], m: 0 }} />

            <Box sx={{ px: 2.5, pt: 1.25, pb: 1.5 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => setMode("new")}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") setMode("new");
                        }}
                        sx={OPTION_SX(mode === "new")}
                    >
                        <Radio
                            size="small"
                            checked={mode === "new"}
                            value="new"
                            sx={{
                                p: 0,
                                color: colors.grey[500],
                                "&.Mui-checked": { color: colors.primary.main },
                            }}
                        />
                        <Box
                            component="span"
                            sx={{
                                fontSize: "0.84rem",
                                fontWeight: 500,
                                color: colors.text.main,
                                lineHeight: 1.4,
                            }}
                        >
                            Create entirely new Order flow
                        </Box>
                    </Box>

                    <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => setMode("append")}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") setMode("append");
                        }}
                        sx={OPTION_SX(mode === "append")}
                    >
                        <Radio
                            size="small"
                            checked={mode === "append"}
                            value="append"
                            sx={{
                                p: 0,
                                color: colors.grey[500],
                                "&.Mui-checked": { color: colors.primary.main },
                            }}
                        />
                        <Box
                            component="span"
                            sx={{
                                fontSize: "0.84rem",
                                fontWeight: 500,
                                color: colors.text.main,
                                lineHeight: 1.4,
                            }}
                        >
                            Append to Existing Order
                        </Box>
                    </Box>
                </Box>

                {mode === "append" && (
                    <Box sx={{ mt: 1.5 }}>
                        <Autocomplete
                            options={orders}
                            loading={loadingOrders}
                            value={selectedOrder}
                            onChange={(_, value) => setSelectedOrder(value)}
                            getOptionLabel={(option) =>
                                option.order_id || option.name || `Order #${option.id}`
                            }
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Order"
                                    placeholder="Search orders..."
                                    size="small"
                                    fullWidth
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "11px",
                                            fontSize: "0.875rem",
                                            "&:hover fieldset": { borderColor: colors.primary.main },
                                            "&.Mui-focused fieldset": {
                                                borderColor: colors.primary.main,
                                                boxShadow: `0 0 0 3px ${colors.brand.primaryContainer}`,
                                            },
                                        },
                                        "& .MuiInputLabel-root.Mui-focused": {
                                            color: colors.primary.main,
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                )}
            </Box>

            <Divider sx={{ borderColor: colors.grey[300], m: 0 }} />

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 1,
                    px: 2.5,
                    pt: 1.25,
                    pb: 1.5,
                }}
            >
                <Button
                    onClick={onClose}
                    sx={{
                        minWidth: 92,
                        py: 0.85,
                        px: 2,
                        borderRadius: "999px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.84rem",
                        color: colors.text.main,
                        border: `1px solid ${colors.grey[400]}`,
                        bgcolor: colors.white.main,
                        "&:hover": {
                            bgcolor: colors.grey[200],
                            borderColor: colors.grey[500],
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleProceed}
                    disabled={mode === "append" && !selectedOrder}
                    sx={{
                        minWidth: 92,
                        py: 0.85,
                        px: 2.5,
                        borderRadius: "999px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.84rem",
                        color: colors.white.main,
                        background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.dark.focus} 100%)`,
                        boxShadow: "0 6px 16px rgba(21, 101, 192, 0.28)",
                        "&:hover": {
                            background: `linear-gradient(135deg, ${colors.primary.focus} 0%, ${colors.primary.main} 100%)`,
                            boxShadow: "0 8px 20px rgba(21, 101, 192, 0.32)",
                        },
                        "&:disabled": {
                            color: colors.grey[600],
                            background: colors.grey[300],
                            boxShadow: "none",
                        },
                    }}
                >
                    Proceed
                </Button>
            </Box>
        </Dialog>
    );
};

OrderAddDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    dialogTitle: PropTypes.string.isRequired,
};

export default OrderAddDialog;
